import React, { createContext, useState, useContext, useEffect } from "react";
import api from "../api/client";
import { useAuth } from "./AuthContext";

const OrganizationContext = createContext();

export function OrganizationProvider({ children }) {
  const { user } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);

  useEffect(() => {
    if (user) {
      fetchOrganization();
    } else {
      setOrganization(null);
      setLoading(false);
    }
  }, [user]);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/v1/organizations/me");
      if (response.data) {
        setOrganization(response.data);
        setMembers(response.data.members || []);
      } else {
        setOrganization(null);
        setMembers([]);
      }
    } catch (error) {
      console.error("Error fetching organization:", error);
      setError("Failed to load organization");
    } finally {
      setLoading(false);
    }
  };

  const createOrganization = async (name, slug) => {
    try {
      const response = await api.post("/api/v1/organizations/", { name, slug });
      setOrganization(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error creating organization:", error);
      return {
        success: false,
        error: error.response?.data?.detail || "Failed to create organization",
      };
    }
  };

  const inviteUser = async (email, role = "member") => {
    try {
      const response = await api.post("/api/v1/organizations/invites", {
        email,
        role,
      });
      setInvites([...invites, response.data]);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error inviting user:", error);
      return {
        success: false,
        error: error.response?.data?.detail || "Failed to invite user",
      };
    }
  };

  const acceptInvite = async (token) => {
    try {
      const response = await api.post("/api/v1/organizations/invites/accept", {
        token,
      });
      await fetchOrganization();
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error accepting invite:", error);
      return {
        success: false,
        error: error.response?.data?.detail || "Failed to accept invite",
      };
    }
  };

  const leaveOrganization = async () => {
    try {
      await api.post("/api/v1/organizations/leave");
      setOrganization(null);
      setMembers([]);
      return { success: true };
    } catch (error) {
      console.error("Error leaving organization:", error);
      return {
        success: false,
        error: error.response?.data?.detail || "Failed to leave organization",
      };
    }
  };

  const value = {
    organization,
    members,
    invites,
    loading,
    error,
    fetchOrganization,
    createOrganization,
    inviteUser,
    acceptInvite,
    leaveOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within OrganizationProvider");
  }
  return context;
}
